import {
	useRef,
	useState,
	useEffect,
	useLayoutEffect,
	type MouseEvent,
	type ReactElement,
} from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { FiAlertCircle, FiLoader } from "react-icons/fi";
import { isContentProcessing, type LibraryItem } from "../lib/useLibrary";
import {
	getItemLocation,
	getItemTitle,
	getItemSummary,
	ITEM_TYPE_DISPLAY,
	navigateToItem,
} from "./libraryShared";

function relativeTime(dateStr: string): string {
	const seconds = Math.floor(
		(Date.now() - new Date(dateStr).getTime()) / 1000,
	);
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `${days}d ago`;
}

function getItemTypeDisplay(item: LibraryItem): string {
	return ITEM_TYPE_DISPLAY[item.item_type] ?? item.item_type;
}

function getItemTags(item: LibraryItem): { id: string; name: string }[] {
	return item.library_item_tags?.map((row) => row.tags) ?? [];
}

function SkeletonRows(): ReactElement {
	return (
		<>
			{[1, 2, 3].map((i) => (
				<tr key={i} className="border-line border-b">
					<td className="py-4 pr-4 pl-4">
						<div className="bg-skeleton h-4 w-48 animate-pulse rounded" />
					</td>
					<td className="py-4 pr-4">
						<div className="bg-skeleton h-4 w-24 animate-pulse rounded" />
					</td>
					<td className="py-4 pr-4">
						<div className="bg-skeleton h-4 w-20 animate-pulse rounded" />
					</td>
					<td className="py-4 pr-4">
						<div className="bg-skeleton h-4 w-16 animate-pulse rounded" />
					</td>
				</tr>
			))}
		</>
	);
}

function TagsCell({
	tags,
	type,
	summary,
}: {
	tags: { id: string; name: string }[];
	type: string | undefined;
	summary: "on" | undefined;
}): ReactElement | null {
	const containerRef = useRef<HTMLDivElement>(null);
	const [visibleCount, setVisibleCount] = useState(tags.length);
	const [measurePass, setMeasurePass] = useState(0);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const observer = new ResizeObserver(() => {
			setVisibleCount(tags.length);
			setMeasurePass((n) => n + 1);
		});
		observer.observe(container);
		return () => observer.disconnect();
	}, [tags.length]);

	useLayoutEffect(() => {
		const container = containerRef.current;
		if (!container || tags.length === 0) return;

		const containerWidth = container.clientWidth;
		const gap = 6; // gap-1.5
		const badgeWidth = 55; // approx width of "+N more"
		let usedWidth = 0;
		let fitCount = 0;

		for (let i = 0; i < container.children.length; i++) {
			const child = container.children[i] as HTMLElement;
			const childWidth = child.offsetWidth;
			const needed = usedWidth + (i > 0 ? gap : 0) + childWidth;

			// Last tag: no badge needed, just check if it fits
			if (i === tags.length - 1 && needed <= containerWidth) {
				fitCount = tags.length;
				break;
			}

			// Check if tag + badge would fit
			if (needed + gap + badgeWidth > containerWidth) {
				break;
			}

			usedWidth = needed;
			fitCount = i + 1;
		}

		if (fitCount === 0 && tags.length > 0) fitCount = 1;
		setVisibleCount(fitCount);
	}, [tags, measurePass]);

	if (tags.length === 0) return null;

	const hiddenCount = tags.length - visibleCount;

	return (
		<div
			ref={containerRef}
			className="flex flex-nowrap items-center gap-1.5 overflow-hidden"
		>
			{tags.slice(0, visibleCount).map((tag) => (
				<Link
					key={tag.id}
					to="/"
					search={{
						mode: "library",
						tag: tag.name,
						type,
						summary,
					}}
					onClick={(e: MouseEvent) => e.stopPropagation()}
					className="tag-pill shrink-0"
				>
					{tag.name}
				</Link>
			))}
			{hiddenCount > 0 && (
				<span className="text-fg-ghost shrink-0 text-xs whitespace-nowrap">
					+{hiddenCount}
				</span>
			)}
		</div>
	);
}

interface LibraryTableProps {
	items: LibraryItem[] | undefined;
	isLoading: boolean;
	showSummary: boolean;
	tag: string | undefined;
	type: string | undefined;
	summary: "on" | undefined;
}

export function LibraryTable({
	items,
	isLoading,
	showSummary,
	tag,
	type,
	summary,
}: LibraryTableProps): ReactElement {
	const navigate = useNavigate();

	if (!isLoading && (!items || items.length === 0)) {
		return (
			<div className="border-line mt-2 border-t py-16 text-center">
				<p className="text-fg-ghost text-sm font-light">
					No items yet. Paste a URL above to get started.
				</p>
			</div>
		);
	}

	return (
		<table className="w-full table-fixed" data-testid="library-table">
			<thead>
				<tr className="border-line border-b text-left">
					<th className="text-fg-ghost w-1/2 pb-3 pl-4 text-[11px] font-normal tracking-widest uppercase">
						Title
					</th>
					<th className="text-fg-ghost w-[120px] pb-3 text-[11px] font-normal tracking-widest uppercase">
						Type
					</th>
					<th className="text-fg-ghost pb-3 text-[11px] font-normal tracking-widest uppercase">
						Tags
					</th>
					<th className="text-fg-ghost w-[100px] pr-4 pb-3 text-right text-[11px] font-normal tracking-widest uppercase">
						Added
					</th>
				</tr>
			</thead>
			<tbody>
				{isLoading ? (
					<SkeletonRows />
				) : (
					items?.map((item) => {
						const status = item.contents?.status;
						const isProcessing = isContentProcessing(item);
						const isFailed =
							item.source_type === "content" &&
							status === "failed";
						const location = getItemLocation(item);
						const typeDisplay = getItemTypeDisplay(item);
						const tags = getItemTags(item);
						const itemSummary = getItemSummary(item);
						return (
							<tr
								key={item.id}
								data-testid="library-row"
								data-content-id={item.content_id}
								data-status={status ?? "completed"}
								onClick={(
									e: MouseEvent<HTMLTableRowElement>,
								) => {
									if (
										(e.target as HTMLElement).closest(
											"a, button, input, select, textarea",
										)
									) {
										return;
									}

									navigateToItem(navigate, item);
								}}
								className="border-line hover:bg-skeleton cursor-pointer border-b transition-colors"
							>
								<td className="text-fg-secondary py-4 pr-4 pl-4 text-sm font-light">
									{location ? (
										<Link
											{...location}
											data-testid="library-row-link"
											className="block"
										>
											<span className="inline-flex max-w-full items-center gap-2.5">
												{isProcessing && (
													<FiLoader className="text-accent h-3.5 w-3.5 shrink-0 animate-spin" />
												)}
												{isFailed && (
													<FiAlertCircle className="h-3.5 w-3.5 shrink-0 text-red-400" />
												)}
												<span className="truncate">
													{getItemTitle(item)}
												</span>
											</span>
											{showSummary && itemSummary && (
												<span
													data-testid="item-summary"
													className="text-fg-faint mt-0.5 block text-xs font-light"
												>
													{itemSummary}
												</span>
											)}
										</Link>
									) : (
										<>
											<span className="inline-flex max-w-full items-center gap-2.5">
												{isProcessing && (
													<FiLoader className="text-accent h-3.5 w-3.5 shrink-0 animate-spin" />
												)}
												{isFailed && (
													<FiAlertCircle className="h-3.5 w-3.5 shrink-0 text-red-400" />
												)}
												<span className="truncate">
													{getItemTitle(item)}
												</span>
											</span>
											{showSummary && itemSummary && (
												<span
													data-testid="item-summary"
													className="text-fg-faint mt-0.5 block text-xs font-light"
												>
													{itemSummary}
												</span>
											)}
										</>
									)}
								</td>
								<td className="text-fg-faint truncate py-4 pr-4 text-sm font-light">
									<Link
										to="/"
										search={{
											mode: "library",
											tag,
											type: item.item_type,
											summary,
										}}
										onClick={(e: MouseEvent) =>
											e.stopPropagation()
										}
										className="hover:text-fg-tertiary transition-colors"
									>
										{typeDisplay}
									</Link>
								</td>
								<td className="py-4 pr-4">
									<TagsCell
										tags={tags}
										type={type}
										summary={summary}
									/>
								</td>
								<td className="text-fg-ghost py-4 pr-4 text-right text-sm font-light">
									{relativeTime(item.added_at)}
								</td>
							</tr>
						);
					})
				)}
			</tbody>
		</table>
	);
}
